import { useEffect, useMemo, useReducer, useState } from 'react';

import { useStatusMessages } from '../../shared/StatusMessages';
import {
  NonStrategicTooltipContent,
  StrategicTooltipContent
} from '../../shared/SupplierStatusTooltip';
import SupplierCommentsPopup from './CommentsPopup';
import SupplierPopup from './SupplierPopup';
import InfoWithPopOver from '../InfoWithPopOver';
import {
  addPropAndValue,
  formatAmountWithCommas,
  getScope,
  getSuppliersWithSroSbmPopulated,
  isScopeAnyGlobalType,
  populateSuppliersRiskRatingMap
} from '../../shared/UtilFunctions';
import {
  AE_RATING_PROP_NAME,
  IDENTIFIED_STATUS_EXCEPTION,
  NOT_AVAILABLE_UPPERCASE,
  RISK_RATING_PROP_NAME,
  SCOPE_LEVEL_GLOBAL_AFTERMARKET,
  SCOPE_LEVEL_GLOBAL_PRODUCTION,
  SCOPE_LEVEL_UNITORUNITGROUP,
  SRO_SBM_PROP_NAME,
  SRO_SBM_USER_ID_PROP_NAME,
  SUPPLIER_STATUS_REDUCE
} from '../../shared/AppConstants';
import MultiSelectColumnFilter from '../../shared/MultiSelectColumnFilter';
import {
  Alert,
  Box,
  CardContent,
  Chip,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { StrategicSupplierDataGrid } from '../supplier_datagrid/SupplierDataGrid';
import { NonStrategicSupplierDataGrid } from '../supplier_datagrid/NonStrategicSupplierDataGrid';
import {
  checkSanity,
  defaultStatus,
  getSelectedScopeStructured
} from '../supplier_datagrid/SupplierDataGridUtils';
import SupplierDataGridByUnits from '../supplier_datagrid/SupplierDataGridByUnits';
import {
  getAERatingMap,
  getSdiMap,
  getSupplierLocationInfo,
  getSuppliersSroSbmMap
} from '../../shared/APIHelper';
import ProspectiveSupplierDataGrid from '../supplier_datagrid/prospective_supplier/ProspectiveSupplierDataGrid';
import { useDispatch, useSelector } from 'react-redux';
import { setProspectiveSuppliers } from '../supplier_datagrid/prospective_supplier/prospectiveSupplierSlice';
import {
  AERatingToolTipText,
  SBM_TOOLTIP_TEXT
} from '../supplier_datagrid/prospective_supplier/PSAppConstants';
import { Link } from 'react-router-dom';
import { getEnvVar } from '../../utils/getEnvVar';
import {
  selectDisabledSideName,
  selectScopeDirty,
  selectScopeType,
  selectSelectedScopeUnits,
  selectSelectedScopeViewList
} from '../Strategy/scopeSlice';

const initialState = {
  loading: true,
  error: '',
  post: []
};

export default function SupplierTabsCard({
  strategicSupplierDataGridApiRef,
  nonStrategicSupplierDataGridApiRef,
  showSensitiveInfo,
  strategyInformation,
  setSuppliersDirty,
  setSuppliersMessage,
  isEdit,
  userInfoMap,
  commonProps,
  supplierScopeOverview,
  collapseExpandedSuppliers,
  setNonStrategicSuppliersList,
  donotEnforceCommentsWhenSupplierTypeChanged,
  setSuppliersData,
  setNonStrategicSuppliersData,
  setCollapseExpandedSuppliers,
  isCreate = false
}) {
  const prospectiveSuppliersRows = useSelector(
    (state) => state.prospectiveSupplier.prospectiveSupplierRows
  );
  const selectedScopeUnitsFromState = useSelector(selectSelectedScopeUnits);
  const selectedScopeViewListFromState = useSelector(
    selectSelectedScopeViewList
  );
  const scopeTypeFromState = useSelector(selectScopeType);
  const scopeDirtyFromState = useSelector(selectScopeDirty);
  const disabledSideNameFromState = useSelector(selectDisabledSideName);
  const { incrementAPIInProgress, decrementAPIInProgress, masterScopeJson } =
    commonProps;
  const riskRatingOnlineAppUrl = getEnvVar(
    'VITE_SUPPLIERS_RISK_RATING_ONLINE_APP_URL'
  );
  const { setStatusErrorMsg } = useStatusMessages();
  const [refreshSupplierList, setRefreshSupplierList] = useState(false);
  const [nonSupplier, setNonSupplier] = useState([]);
  const [refreshNonStrategicSupplierList, setRefreshNonStrategicSupplierList] =
    useState(true);
  const dispatch = useDispatch();
  const [suppliersNameInfoMap, setSuppliersNameInfoMap] = useState(null);
  const [suppliersVitalInfoMap, setSuppliersVitalInfoMap] = useState(null);
  const [supplierRiskRatingMap, setSupplierRiskRatingMap] = useState(null);
  const [refreshSupplierSpend, setRefreshSupplierSpend] = useState(false);
  const [showSupplierCommentsModal, setShowSupplierCommentsModal] =
    useState(false);
  const handleModalCancel = () => setShowSupplierCommentsModal(false);

  const VIEW_BY_SPLR = 'bySplr';
  const VIEW_BY_UNIT = 'byUnit';
  const [viewType, setViewType] = useState(VIEW_BY_SPLR);
  const [activeTab, setActiveTab] = useState(0);

  const [supplierDetails, setSupplierDetails] = useState();
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [editNonStrategic, setEditNonStrategic] = useState(false);
  const [
    showModalEditNonStrategicSupplier,
    setShowModalEditNonStrategicSupplier
  ] = useState(false);
  const [unitGroups, setUnitGroups] = useState(null);
  const scopeFromSlice = useMemo(() => {
    if (!isEdit) {
      return null;
    }

    const hasScopeSelection = (scope) => {
      if (!scope?.level) {
        return false;
      }

      if (isScopeAnyGlobalType(scope.level)) {
        return true;
      }

      return (
        (Array.isArray(scope.unitGroups) && scope.unitGroups.length > 0) ||
        (Array.isArray(scope.units) && scope.units.length > 0)
      );
    };

    if (isScopeAnyGlobalType(scopeTypeFromState)) {
      return {
        level: scopeTypeFromState,
        unitGroups: [],
        units: []
      };
    }

    if (
      Array.isArray(selectedScopeUnitsFromState) &&
      selectedScopeUnitsFromState.length > 0 &&
      scopeTypeFromState
    ) {
      const scopeFromUnits = getScope(
        scopeTypeFromState,
        selectedScopeUnitsFromState
      );

      if (hasScopeSelection(scopeFromUnits)) {
        return scopeFromUnits;
      }
    }

    if (
      !scopeDirtyFromState &&
      hasScopeSelection(selectedScopeViewListFromState)
    ) {
      return selectedScopeViewListFromState;
    }

    return null;
  }, [
    isEdit,
    scopeTypeFromState,
    selectedScopeUnitsFromState,
    scopeDirtyFromState,
    selectedScopeViewListFromState
  ]);
  const effectiveSupplierScopeOverview = useMemo(
    () =>
      scopeDirtyFromState
        ? scopeFromSlice
        : scopeFromSlice || supplierScopeOverview || strategyInformation?.scope,
    [
      scopeDirtyFromState,
      scopeFromSlice,
      supplierScopeOverview,
      strategyInformation?.scope
    ]
  );
  const effectiveScopeSide = useMemo(() => {
    const scopeLevel = effectiveSupplierScopeOverview?.level;

    if (isScopeAnyGlobalType(scopeLevel)) {
      return scopeLevel;
    }

    if (disabledSideNameFromState === SCOPE_LEVEL_GLOBAL_AFTERMARKET) {
      return SCOPE_LEVEL_GLOBAL_PRODUCTION;
    }
    if (disabledSideNameFromState === SCOPE_LEVEL_GLOBAL_PRODUCTION) {
      return SCOPE_LEVEL_GLOBAL_AFTERMARKET;
    }

    return null;
  }, [effectiveSupplierScopeOverview, disabledSideNameFromState]);
  const masterScopeGroupsForSupplier = useMemo(() => {
    const globalLevelMap = masterScopeJson?.globalLevelMap;
    if (globalLevelMap && effectiveScopeSide) {
      return globalLevelMap[effectiveScopeSide] || [];
    }

    if (
      Array.isArray(masterScopeJson?.unitGroups) &&
      masterScopeJson.unitGroups.length > 0
    ) {
      return masterScopeJson.unitGroups;
    }

    if (!globalLevelMap) {
      return [];
    }

    const allGroups = [
      ...(globalLevelMap[SCOPE_LEVEL_GLOBAL_PRODUCTION] || []),
      ...(globalLevelMap[SCOPE_LEVEL_GLOBAL_AFTERMARKET] || [])
    ];
    const uniqueGroups = new Map();

    allGroups.forEach((group) => {
      if (group?.code && !uniqueGroups.has(group.code)) {
        uniqueGroups.set(group.code, group);
      }
    });

    return Array.from(uniqueGroups.values());
  }, [masterScopeJson, effectiveScopeSide]);
  const unitGroupsFromSelectedScopeUnits = useMemo(() => {
    if (
      scopeTypeFromState !== SCOPE_LEVEL_UNITORUNITGROUP ||
      !Array.isArray(selectedScopeUnitsFromState)
    ) {
      return null;
    }

    const nextUnitGroups = selectedScopeUnitsFromState
      .map((unitGroup) => {
        const selectedUnits = (unitGroup.children || []).filter(
          (unit) => unit?.checked
        );

        if (!unitGroup?.checked && selectedUnits.length === 0) {
          return null;
        }

        return {
          code: unitGroup.code,
          name: unitGroup.name,
          notFullySelected: !unitGroup.checked,
          units: unitGroup.checked ? unitGroup.children || [] : selectedUnits
        };
      })
      .filter(Boolean);

    if (nextUnitGroups.length > 0) {
      return nextUnitGroups;
    }

    return scopeDirtyFromState ? [] : null;
  }, [scopeTypeFromState, selectedScopeUnitsFromState, scopeDirtyFromState]);

  function getAndSetStatusMap(obj) {
    let statusMap = obj.statusMap;

    if (!statusMap) {
      statusMap = new Map();
      addPropAndValue(obj, 'statusMap', statusMap);
    }
    return statusMap;
  }

  function checkAndRemoveEntry(status, map, key, value, valueComplex) {
    let itemStatus = valueComplex ? value.status : value;

    if (itemStatus === status) map.delete(key);
  }

  function removeMapEntries(map, status, valueComplex) {
    const iterator1 = map[Symbol.iterator]();

    for (const item of iterator1)
      checkAndRemoveEntry(status, map, item[0], item[1], valueComplex);
  }

  function doUnitGroupStatusAdjustments(
    statusMap,
    splrObj,
    unitGroupStatusObj,
    unitGroupCode
  ) {
    // Check self status
    if (
      !unitGroupStatusObj.status ||
      unitGroupStatusObj.status === splrObj.status
    ) {
      if (
        !unitGroupStatusObj.statusMap ||
        unitGroupStatusObj.statusMap.size === 0
      )
        // Ok to Delete the entry
        statusMap.delete(unitGroupCode);
      // Remove Status prop
      else delete unitGroupStatusObj.status;
    }
  }

  function doStatusHousekeeping(statusMap, splrObj) {
    const iterator = statusMap[Symbol.iterator]();
    let itemStatus;
    let unitGroupStatusObj;

    for (const item of iterator) {
      // [0] - key, [1] - value
      if (item[0] !== defaultStatus) {
        unitGroupStatusObj = item[1];
        itemStatus = unitGroupStatusObj.status;

        if (unitGroupStatusObj.statusMap)
          removeMapEntries(
            unitGroupStatusObj.statusMap,
            itemStatus || splrObj.status, // Use self status or Supplier's
            false
          );

        doUnitGroupStatusAdjustments(
          statusMap,
          splrObj,
          unitGroupStatusObj,
          item[0]
        );
      }
    }
  }

  function updateSupplierStatusInternal(
    splrObj,
    { status, scope, parentScope }
  ) {
    checkSanity(scope, parentScope);

    const statusMap = getAndSetStatusMap(splrObj);

    // Add to Map
    if (parentScope) {
      // For Unit

      // Entry should be present in main map
      let unitGroupStatusObj = statusMap.get(parentScope);

      if (!unitGroupStatusObj) {
        // Unit Group Entry missing
        unitGroupStatusObj = {};
        statusMap.set(parentScope, unitGroupStatusObj);
      }

      // Update Unit Status
      getAndSetStatusMap(unitGroupStatusObj).set(scope, status);
    } else if (scope) {
      // For Unit Group

      // Entry should be present in main map
      const unitGroupStatusObj = statusMap.get(scope);

      if (unitGroupStatusObj) {
        addPropAndValue(unitGroupStatusObj, 'status', status);
        // Remove Unit Entries which now have same Status as Unit Group
        if (unitGroupStatusObj.statusMap)
          removeMapEntries(unitGroupStatusObj.statusMap, status, true);
      } else statusMap.set(scope, { status: status });
    } else {
      // Supplier

      // Set on Supplier Obj
      splrObj.status = status;
    }

    doStatusHousekeeping(statusMap, splrObj);
  }

  const strategicSupplierReducer = (state, action) => {
    switch (action.type) {
      case 'DATA_OVERRIDE':
      case 'FETCH_SUCCESS':
        return {
          loading: false,
          post: populateSupplierOtherInfo(action.payload, action.type),
          error: ''
        };
      case 'FETCH_FAIL':
        return {
          loading: false,
          post: [],
          error: 'Something went wrong!'
        };
      case 'add': {
        // Always add as first element
        let objArray = [action.payload, ...state.post];

        return {
          loading: false,
          post: populateSupplierOtherInfo(objArray, action.type),
          error: ''
        };
      }
      case 'editFromGrid': {
        let objArray = [...state.post];
        let newObj = action.payload;

        let obj = objArray.find(
          (obj) => obj.supplierNumber === newObj.supplierNumber
        );

        if (newObj.hasOwnProperty('comments')) {
          obj.comments = newObj.comments;
        }
        if (newObj.hasOwnProperty('status')) {
          updateSupplierStatusInternal(obj, newObj);
        }

        return {
          loading: false,
          post: populateSupplierOtherInfo(objArray, action.type),
          error: ''
        };
      }
      case 'edit': {
        let objArray = [...state.post];

        // Find the edited item index
        let index = objArray.findIndex(
          (obj) => obj.supplierNumber === action.payload[0].supplierNumber
        );

        // Assign Risk Rating from prev. object
        action.payload[0].riskRating = objArray[index].riskRating;

        objArray[index] = action.payload[0];

        return {
          loading: false,
          post: populateSupplierOtherInfo(objArray, action.type),
          error: ''
        };
      }

      case 'del': {
        let objArray = [...state.post];

        // Find the deleted item index
        let indexToDelete = objArray.findIndex(
          (obj) => obj.supplierNumber === action.payload.supplierNumber
        );
        objArray.splice(indexToDelete, 1);

        return {
          loading: false,
          post: populateSupplierOtherInfo(objArray, action.type),
          error: ''
        };
      }
      default:
        return state;
    }
  };

  const nonStrategicSupplierReducer = (state, action) => {
    switch (action.type) {
      case 'FETCH_SUCCESS':
        return {
          loading: false,
          post: action.payload,
          error: '',
          lastActionType: action.type
        };
      case 'FETCH_FAIL':
        return {
          loading: false,
          post: [],
          error: 'Something went wrong!',
          lastActionType: action.type
        };
      case 'add': {
        let objArray = [...state.post].concat(action.payload);

        return {
          loading: false,
          post: objArray,
          error: '',
          lastActionType: action.type
        };
      }
      case 'edit': {
        let objArray = [...state.post];

        // Find the edited item index
        let index = objArray.findIndex(
          (obj) => obj.supplierNumber === action.payload[0].supplierNumber
        );

        // Assign Risk Rating from prev. object
        action.payload[0].riskRating = objArray[index].riskRating;

        objArray[index] = action.payload[0];

        return {
          loading: false,
          post: populateNonStrategicSupplierOtherInfo(objArray, action.type),
          error: '',
          lastActionType: action.type
        };
      }

      case 'editFromGrid': {
        let objArray = [...state.post];
        let newObj = action.payload;

        let obj = objArray.find(
          (obj) => obj.supplierNumber === newObj.supplierNumber
        );

        if (newObj.hasOwnProperty('comments')) {
          obj.comments = newObj.comments;
        }
        if (newObj.hasOwnProperty('status')) {
          updateSupplierStatusInternal(obj, newObj);
        }

        return {
          loading: false,
          post: populateNonStrategicSupplierOtherInfo(objArray, action.type),
          error: '',
          lastActionType: action.type
        };
      }

      case 'del': {
        let objArray = [...state.post];

        // Find the deleted item index
        let indexToDelete = objArray.findIndex(
          (obj) => obj.supplierNumber === action.payload.supplierNumber
        );
        objArray.splice(indexToDelete, 1);

        return {
          loading: false,
          post: objArray,
          error: '',
          lastActionType: action.type
        };
      }

      default:
        return state;
    }
  };

  const [suppliersData, dispatchSupplier] = useReducer(
    strategicSupplierReducer,
    initialState
  );
  const [nonStrategicSuppliersData, dispatchNonStrategicSupplier] = useReducer(
    nonStrategicSupplierReducer,
    initialState
  );

  function clearAllStrategicSupplierExceptions() {
    const newSuppliers = suppliersData?.post.map((splr) => {
      return { ...splr, statusMap: null };
    });
    dispatchSupplier({
      type: 'DATA_OVERRIDE',
      payload: newSuppliers
    });
  }

  function hasDuplicateSuppliers(payload) {
    return suppliersData?.post?.find(
      (obj) => obj.supplierNumber === payload.supplierNumber
    );
  }

  function hasDuplicateNonStrategicSuppliers(payload) {
    return nonStrategicSuppliersData?.post?.find(
      (obj) => obj.supplierNumber === payload.supplierNumber
    );
  }

  function populateSupplierOtherInfo(current, actionType) {
    if (setSuppliersData && actionType !== 'FETCH_SUCCESS') {
      setSuppliersMessage &&
        setSuppliersMessage(
          'Changes to supplier will not take effect until strategy is saved.'
        );

      setSuppliersData(current);
    }

    return current;
  }

  function populateNonStrategicSupplierOtherInfo(current, actionType) {
    if (setNonStrategicSuppliersData && actionType !== 'FETCH_SUCCESS') {
      setSuppliersMessage &&
        setSuppliersMessage(
          'Changes to supplier will not take effect until strategy is saved.'
        );

      setNonStrategicSuppliersData(current);
    }
    return current;
  }

  function handlePromiseResult(result) {
    if (result.status === 'fulfilled') return result.value;
  }

  async function populateSupplierNameMaps(suppliers) {
    if (suppliers && suppliers.length > 0) {
      incrementAPIInProgress();

      const supplierList = suppliers.map((item) => item.supplierNumber);

      try {
        const { nameMap, locationMap, supplierStatusMap } =
          await getSupplierLocationInfo(supplierList);

        const vitalMap = new Map();

        vitalMap.set('nameMap', nameMap);
        vitalMap.set('locationMap', locationMap);
        vitalMap.set('activeSupplierMap', supplierStatusMap);

        setSuppliersNameInfoMap(vitalMap);
      } catch (supplierLocationPromise) {
        setStatusErrorMsg(
          "Couldn't load the Supplier Details. Please try again.",
          new Error(supplierLocationPromise.message)
        );
      } finally {
        decrementAPIInProgress();
      }
    }
  }

  async function populateSupplierOtherVitalInfoMaps(suppliers) {
    if (suppliers && suppliers.length > 0) {
      const supplierList = suppliers.map((item) => item.supplierNumber);
      const promises = [];

      promises.push(getSdiMap(supplierList));
      promises.push(getAERatingMap(supplierList));
      promises.push(getSuppliersSroSbmMap(supplierList));

      const [sdiMapResponsePromise, aeRatingMapPromise, sroSbmMapPromise] =
        await Promise.allSettled(promises);

      const sdiMapResponse = handlePromiseResult(sdiMapResponsePromise);
      const aeRatingMap = handlePromiseResult(aeRatingMapPromise);
      const sroSbmMap = handlePromiseResult(sroSbmMapPromise);

      const nameLocationAndSDIMap = new Map();

      nameLocationAndSDIMap.set('sdiMap', sdiMapResponse);
      nameLocationAndSDIMap.set('aeRatingMap', aeRatingMap);
      nameLocationAndSDIMap.set('sroSbmMap', sroSbmMap);

      setSuppliersVitalInfoMap(nameLocationAndSDIMap);
    }
  }

  useEffect(() => {
    let updateSupplierData = false;

    if (unitGroups && suppliersData?.post) {
      const selectedUnitGroupAndUnitMap = getSelectedUnitGroupAndUnit();
      const splrs = [...suppliersData.post];
      for (let splr of splrs) {
        if (
          splr?.statusMap &&
          splr.statusMap.size > 0 &&
          selectedUnitGroupAndUnitMap.size > 0
        ) {
          for (let [unitGroupCode, unitGroupObj] of splr.statusMap.entries()) {
            let isNotFullySelected =
              selectedUnitGroupAndUnitMap.get(unitGroupCode);

            if (isNotFullySelected && unitGroupObj?.status !== splr.status) {
              unitGroupObj.status = splr.status;
              updateSupplierData = true;
            }
            if (!selectedUnitGroupAndUnitMap.has(unitGroupCode)) {
              splr.statusMap.delete(unitGroupCode);
              updateSupplierData = true;
            } else if (unitGroupObj?.statusMap)
              for (let [
                unitCode,
                unitStatus
              ] of unitGroupObj?.statusMap.entries()) {
                if (
                  !selectedUnitGroupAndUnitMap.has(unitCode) ||
                  (isNotFullySelected && unitStatus === splr.status)
                ) {
                  updateSupplierData = true;
                  unitGroupObj.statusMap.delete(unitCode);
                  if (unitGroupObj.statusMap.size === 0)
                    splr.statusMap.delete(unitGroupCode);
                }
              }
          }
        }
      }
      if (updateSupplierData) {
        setCollapseExpandedSuppliers && setCollapseExpandedSuppliers(true);
        dispatchSupplier({
          type: 'DATA_OVERRIDE',
          payload: splrs
        });
      }
    }
  }, [unitGroups]);

  useEffect(() => {
    if (setNonStrategicSuppliersList && nonStrategicSuppliersData?.post) {
      // Data loads/fetch failures/enrichment refreshes aren't user edits, so don't mark dirty.
      const isNonUserUpdate =
        nonStrategicSuppliersData.lastActionType == null ||
        nonStrategicSuppliersData.lastActionType === 'FETCH_SUCCESS' ||
        nonStrategicSuppliersData.lastActionType === 'FETCH_FAIL';
      setNonStrategicSuppliersList(
        nonStrategicSuppliersData.post,
        isNonUserUpdate
      );
    }
    // setNonStrategicSuppliersList is an unmemoized prop function, intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonStrategicSuppliersData.post, nonStrategicSuppliersData.lastActionType]);

  function getSelectedUnitGroupAndUnit() {
    const unitGroupMap = new Map(
      unitGroups.map((unitGroup) => [
        unitGroup.code,
        unitGroup?.notFullySelected ? unitGroup.notFullySelected : false
      ])
    );
    const unitMap = new Map(
      unitGroups
        .flatMap((unitGroup) => unitGroup.units)
        .map((unit) => [unit.code, unit.code])
    );

    return new Map([...unitGroupMap, ...unitMap]);
  }

  useEffect(() => {
    const selectedUnitGroups =
      unitGroupsFromSelectedScopeUnits !== null
        ? unitGroupsFromSelectedScopeUnits
        : effectiveSupplierScopeOverview
          ? getSelectedScopeStructured(
              masterScopeGroupsForSupplier,
              effectiveSupplierScopeOverview,
              masterScopeJson
            )
          : [];

    setUnitGroups(selectedUnitGroups || []);
  }, [
    effectiveSupplierScopeOverview,
    unitGroupsFromSelectedScopeUnits,
    masterScopeGroupsForSupplier,
    masterScopeJson
  ]);

  useEffect(() => {
    // declare the async data fetching function
    const fetchData = async () => {
      let map = new Map();

      strategyInformation?.nonStrategicSuppliers?.map((supplier) =>
        map.set(supplier.supplierNumber, supplier)
      );

      map.size > 0 &&
        strategyInformation.nonStrategySupplierList?.forEach((supplier) => {
          let obj = map.get(supplier.supplierNumber);

          if (obj) {
            addPropAndValue(obj, 'comments', supplier.comments);
            addPropAndValue(obj, 'updatedById', supplier.updatedById);
            addPropAndValue(obj, 'status', supplier.status);
          }
        });
      setNonSupplier(strategyInformation.nonStrategySupplierList);
      await loadData(
        strategyInformation.suppliers,
        strategyInformation.nonStrategicSuppliers,
        strategyInformation.currentProspectiveSuppliers,
        strategyInformation.nonCurrentProspectiveSuppliers
      );
    };

    // call the function
    if (strategyInformation) fetchData();
  }, [strategyInformation]);

  function updateSupplierCommentsFromGrid(splrNumber, comments) {
    dispatchSupplier({
      type: 'editFromGrid',
      payload: {
        supplierNumber: splrNumber,
        comments: comments
      }
    });
  }

  function updateNonStrategicSupplierCommentsFromGrid(splrNumber, comments) {
    dispatchNonStrategicSupplier({
      type: 'editFromGrid',
      payload: {
        supplierNumber: splrNumber,
        comments: comments
      }
    });
  }

  function updateSupplierStatusFromGrid(
    splrNumber,
    status,
    statusScope,
    parentScope
  ) {
    if (status !== defaultStatus || status !== SUPPLIER_STATUS_REDUCE)
      dispatchSupplier({
        type: 'editFromGrid',
        payload: {
          supplierNumber: splrNumber,
          status: status,
          scope: statusScope,
          parentScope: parentScope
        }
      });
  }

  function updateNonStrategicSupplierStatusFromGrid(
    splrNumber,
    status,
    statusScope,
    parentScope
  ) {
    if (status !== defaultStatus || status !== SUPPLIER_STATUS_REDUCE)
      dispatchNonStrategicSupplier({
        type: 'editFromGrid',
        payload: {
          supplierNumber: splrNumber,
          status: status,
          scope: statusScope,
          parentScope: parentScope
        }
      });
  }

  function loadData(
    strategicSupplierObjs,
    nonStrategicSupplierObjs,
    currentProspectiveSupplierObjs,
    nonCurrentProspectiveSupplierObjs
  ) {
    if (
      strategicSupplierObjs?.length > 0 ||
      nonStrategicSupplierObjs?.length > 0 ||
      currentProspectiveSupplierObjs?.length > 0 ||
      nonCurrentProspectiveSupplierObjs?.length > 0
    ) {
      if (strategicSupplierObjs?.length > 0)
        dispatchSupplier({
          type: 'FETCH_SUCCESS',
          payload: strategicSupplierObjs
        });
      if (nonStrategicSupplierObjs?.length > 0)
        dispatchNonStrategicSupplier({
          type: 'FETCH_SUCCESS',
          payload: nonStrategicSupplierObjs
        });
      // Combine all suppliers for info population
      const allSuppliers = [
        ...strategicSupplierObjs,
        ...nonStrategicSupplierObjs,
        ...currentProspectiveSupplierObjs
      ];
      populateSupplierNameMaps(allSuppliers);
      populateSupplierOtherVitalInfoMaps(allSuppliers);

      // Load Risk Rating
      if (showSensitiveInfo) {
        const supplierNumbers = [
          ...strategicSupplierObjs,
          ...nonStrategicSupplierObjs
        ].map((item) => item.supplierNumber);
        populateSuppliersRiskRatingMap(
          supplierNumbers,
          setSupplierRiskRatingMap,
          commonProps
        );
        setRefreshNonStrategicSupplierList(true);
      }
    }
  }

  useEffect(() => {
    setRefreshSupplierList(!refreshSupplierList);
    setRefreshNonStrategicSupplierList(!refreshNonStrategicSupplierList);
  }, [suppliersData.post, nonStrategicSuppliersData.post, nonSupplier]);

  function getSuppliersWithRiskRatingPopulated(supplierObj) {
    if (supplierObj.length > 0) {
      supplierObj.forEach((obj) => {
        const rating = supplierRiskRatingMap.get(obj.supplierNumber);

        addPropAndValue(
          obj,
          RISK_RATING_PROP_NAME,
          rating || NOT_AVAILABLE_UPPERCASE
        );
      });
      return supplierObj;
    }
  }

  function getSuppliersWithAERatingPopulated(supplierAERatingMap, suppliers) {
    if (suppliers?.length > 0 && supplierAERatingMap) {
      suppliers.forEach((obj) => {
        const rating = supplierAERatingMap.get(obj.supplierNumber);

        addPropAndValue(obj, AE_RATING_PROP_NAME, rating);
      });
      return suppliers;
    }
  }

  function getSuppliersWithPropPopulated(suppliersMap, supplierObj, propName) {
    if (supplierObj && supplierObj.length > 0) {
      supplierObj.forEach((obj) => {
        let txt = suppliersMap?.get(obj.supplierNumber);

        if (!txt) txt = propName === 'supplierName' ? obj.supplierNumber : '';

        addPropAndValue(obj, propName, txt);
      });

      return supplierObj;
    }
  }

  useEffect(() => {
    if (suppliersVitalInfoMap) {
      const tempStrategicData = [...suppliersData.post];
      const tempNonStrategicData = [...nonStrategicSuppliersData.post];
      const supplierSDIMap = suppliersVitalInfoMap.get('sdiMap');
      const aeRatingMap = suppliersVitalInfoMap.get('aeRatingMap');
      const sroSbmMap = suppliersVitalInfoMap.get('sroSbmMap');

      const propertiesToPopulate = [{ map: supplierSDIMap, propName: 'sdis' }];

      propertiesToPopulate.forEach(({ map, propName }) => {
        getSuppliersWithPropPopulated(map, tempStrategicData, propName);
        getSuppliersWithPropPopulated(map, tempNonStrategicData, propName);
      });

      // // Populate AE Ratings
      getSuppliersWithAERatingPopulated(aeRatingMap, tempStrategicData);
      getSuppliersWithAERatingPopulated(aeRatingMap, tempNonStrategicData);

      getSuppliersWithSroSbmPopulated(sroSbmMap, tempStrategicData);
      getSuppliersWithSroSbmPopulated(sroSbmMap, tempNonStrategicData);

      dispatchSupplier({
        type: 'FETCH_SUCCESS',
        payload: tempStrategicData
      });
      dispatchNonStrategicSupplier({
        type: 'FETCH_SUCCESS',
        payload: tempNonStrategicData
      });
    }
  }, [suppliersVitalInfoMap]);

  useEffect(() => {
    if (suppliersNameInfoMap) {
      const tempStrategicData = [...suppliersData.post];
      const tempNonStrategicData = [...nonStrategicSuppliersData.post];
      const supplierNameMap = suppliersNameInfoMap.get('nameMap');
      const supplierLocationMap = suppliersNameInfoMap.get('locationMap');
      const activeSupplierMap = suppliersNameInfoMap.get('activeSupplierMap');

      const propertiesToPopulate = [
        { map: supplierNameMap, propName: 'supplierName' },
        { map: activeSupplierMap, propName: 'activeSupplier' },
        { map: supplierLocationMap, propName: 'supplierLocation' }
      ];

      propertiesToPopulate.forEach(({ map, propName }) => {
        getSuppliersWithPropPopulated(map, tempStrategicData, propName);
        getSuppliersWithPropPopulated(map, tempNonStrategicData, propName);
      });

      dispatchSupplier({
        type: 'FETCH_SUCCESS',
        payload: tempStrategicData
      });
      dispatchNonStrategicSupplier({
        type: 'FETCH_SUCCESS',
        payload: tempNonStrategicData
      });
    }
  }, [suppliersNameInfoMap]);

  useEffect(() => {
    if (!suppliersVitalInfoMap || !suppliersNameInfoMap) return;

    const jointMap = {};

    // Add suppliersNameInfoMap entries to jointMap
    suppliersNameInfoMap.forEach(
      (value, key) => (jointMap[key] = value ? Object.fromEntries(value) : {})
    );

    // Add suppliersVitalInfoMap entries to jointMap
    suppliersVitalInfoMap.forEach(
      (value, key) => (jointMap[key] = value ? Object.fromEntries(value) : {})
    );

    dispatch(
      setProspectiveSuppliers({
        currentProspectiveSuppliers:
          strategyInformation.currentProspectiveSuppliers,
        nonCurrentProspectiveSuppliers:
          strategyInformation.nonCurrentProspectiveSuppliers,
        suppliersVitalInfoMap: jointMap
      })
    );
  }, [suppliersNameInfoMap, suppliersVitalInfoMap]);

  useEffect(() => {
    if (supplierRiskRatingMap) {
      const tempStrategicData = [...suppliersData.post];
      const tempNonStrategicData = [...nonStrategicSuppliersData.post];
      getSuppliersWithRiskRatingPopulated(tempStrategicData);

      getSuppliersWithRiskRatingPopulated(tempNonStrategicData);

      dispatchSupplier({
        type: 'FETCH_SUCCESS',
        payload: tempStrategicData
      });
      dispatchNonStrategicSupplier({
        type: 'FETCH_SUCCESS',
        payload: tempNonStrategicData
      });
    }
  }, [supplierRiskRatingMap]);

  function openLink(e, path) {
    e.preventDefault();

    window.open(path, 'risk-window');
  }

  function openComment(e, supplier) {
    e.preventDefault();
    setShowSupplierCommentsModal(true);
    setSupplierDetails(supplier);
  }

  function getTableColumnHeaderInfo(forStrategic) {
    let cols = [
      {
        Header: 'Number',
        accessor: (d) => d.supplierNumber,
        Filter: MultiSelectColumnFilter,
        filter: 'multiple',
        Cell: (params) => {
          const isBlockedSupplier = params.row.original.activeSupplier === 'N';
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>{params.value}</span>
              {isBlockedSupplier && (
                <Tooltip title="Blocked">
                  <Box
                    component="span"
                    sx={(theme) => ({
                      color: theme.fuel6Tokens.JddcColorIconError,
                      marginLeft: '0.25em',
                      display: 'inline-flex'
                    })}
                  >
                    <fuel-icon name="do_not_disturb_on" />
                  </Box>
                </Tooltip>
              )}
            </div>
          );
        }
      },
      {
        Header: 'Name',
        accessor: (d) => d.supplierName,
        Filter: MultiSelectColumnFilter,
        filter: 'multiple'
      },
      {
        Header: 'Status',
        accessor: (d) => d.statusDesc || d.status,
        Filter: MultiSelectColumnFilter,
        filter: 'multiple',
        tipText: forStrategic ? (
          <StrategicTooltipContent />
        ) : (
          <NonStrategicTooltipContent />
        )
      }
    ];

    if (showSensitiveInfo) {
      cols.push({
        Header: 'R12 Spend',
        accessor: (d) => d.invoiceSpend,
        align: 'right',
        Cell: (row) => formatAmountWithCommas(row.value, true)
      });

      cols.push({
        Header: 'R12 Forecast',
        accessor: (d) => d.forecastSpend,
        align: 'right',
        Cell: (row) => formatAmountWithCommas(row.value, true)
      });

      cols.push({
        Header: 'Forecast %',
        accessor: (d) => d.forecastSpendPercentage,
        align: 'right',
        Cell: (row) => (row.value ? Math.round(row.value) : '0') + '%'
      });

      cols.push({
        Header: 'Risk Rating',
        accessor: (d) => d.riskRating,
        Filter: MultiSelectColumnFilter,
        filter: 'multiple',
        Cell: (row) => {
          return (
            <Link
              onClick={(e) =>
                openLink(
                  e,
                  riskRatingOnlineAppUrl + row.row.original.supplierNumber
                )
              }
            >
              {row.value}
            </Link>
          );
        }
      });
    }

    cols.push({
      Header: 'SD Indicators',
      accessor: (d) =>
        d.sdis?.length > 0 ? <InfoWithPopOver infoList={d.sdis} /> : <></>,
      disableSortBy: true
    });

    cols.push({
      Header: 'AE Rating',
      accessor: (d) => d.aeRating,
      Filter: MultiSelectColumnFilter,
      filter: 'multiple',
      tipText: AERatingToolTipText
    });
    cols.push({
      Header: 'SBM',
      accessor: (d) => d[SRO_SBM_PROP_NAME],
      Filter: MultiSelectColumnFilter,
      filter: 'multiple',
      Cell: (row) => (
        <Tooltip title={row.row.original[SRO_SBM_USER_ID_PROP_NAME]}>
          {row.row.original[SRO_SBM_PROP_NAME]}
        </Tooltip>
      ),
      tipText: SBM_TOOLTIP_TEXT
    });
    cols.push({
      Header: 'Comments',
      accessor: (d) => d.comments,
      Filter: MultiSelectColumnFilter,
      filter: 'multiple',
      Cell: (row) => {
        return (
          <div
            style={{
              width: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={row.value && row.value.replaceAll(/(<([^</>]+)>)/gi, ' ')}
          >
            <Link onClick={(e) => openComment(e, row.row.original)}>
              {row.value}
            </Link>
          </div>
        );
      }
    });
    return cols;
  }

  const nonStrategicColumns = useMemo(
    () => getTableColumnHeaderInfo(false),
    [nonStrategicSuppliersData.post, showSensitiveInfo]
  );

  const tableInitialState = useMemo(() => {
    return {
      pageIndex: 0,
      pageSize: 300,
      sortBy: showSensitiveInfo
        ? [
            {
              id: 'Forecasted 52 Week Spend',
              desc: true
            },
            {
              id: 'Number',
              asc: true
            }
          ]
        : [
            {
              id: 'Name',
              asc: true
            }
          ]
    };
  }, [showSensitiveInfo]);

  function editNonStrategicSuppliers(supplier) {
    setSupplier(supplier);
    setEditNonStrategic(true);
    setShowModalEditNonStrategicSupplier(true);
  }

  const handleChange = (event, newOption) => {
    if (newOption) setViewType(newOption);
  };

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_e, newValue) => setActiveTab(newValue)}
        aria-label="supplier tabs"
      >
        <Tab
          id="supplier-tab-0"
          label={
            <TabLabel
              label="Strategic Suppliers"
              count={suppliersData?.post?.length}
            />
          }
        />
        <Tab
          id="supplier-tab-1"
          label={
            <TabLabel
              label="Non-Strategic Suppliers"
              count={nonStrategicSuppliersData?.post?.length}
            />
          }
        />
        {!isCreate && (
          <Tab
            id="supplier-tab-2"
            label={
              <TabLabel
                label="Prospective Suppliers"
                count={prospectiveSuppliersRows?.length}
              />
            }
          />
        )}
      </Tabs>

      <Box role="tabpanel" hidden={activeTab !== 0}>
        {activeTab === 0 && (
          <CardContent>
            <Grid container>
              <Grid size={6}>
                <ToggleButtonGroup
                  aria-label="View By Suppliers or Units"
                  exclusive
                  onChange={handleChange}
                  value={viewType}
                  variant={'segmented'}
                >
                  <ToggleButton value={VIEW_BY_SPLR} key="1">
                    By Supplier
                  </ToggleButton>
                  <ToggleButton value={VIEW_BY_UNIT} key="2">
                    By Unit
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {!isCreate && (
                <Grid
                  size={6}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Chip
                    color="informational"
                    variant="badge"
                    label={IDENTIFIED_STATUS_EXCEPTION}
                    size="small"
                  />
                </Grid>
              )}
              <Grid size={12} mt={4}>
                {viewType === VIEW_BY_SPLR ? (
                  <StrategicSupplierDataGrid
                    apiRef={strategicSupplierDataGridApiRef}
                    isEdit={isEdit}
                    isCreate={isCreate}
                    strategyInformation={strategyInformation}
                    strategicSuppliers={suppliersData.post}
                    commonProps={commonProps}
                    unitGroups={unitGroups}
                    updateSupplierComments={updateSupplierCommentsFromGrid}
                    updateSupplierStatus={updateSupplierStatusFromGrid}
                    dispatchSupplier={dispatchSupplier}
                    dispatchNonStrategicSupplier={dispatchNonStrategicSupplier}
                    hasDuplicateSuppliers={hasDuplicateSuppliers}
                    hasDuplicateNonStrategicSuppliers={
                      hasDuplicateNonStrategicSuppliers
                    }
                    setNonStrategicSuppliers={setNonStrategicSuppliersList}
                    setNonSupplier={setNonSupplier}
                    nonSupplier={nonSupplier}
                    collapseExpandedSuppliers={collapseExpandedSuppliers}
                    setCollapseExpandedSuppliers={setCollapseExpandedSuppliers}
                    clearAllStrategicSupplierExceptions={
                      clearAllStrategicSupplierExceptions
                    }
                  />
                ) : (
                  <>
                    {(!suppliersData || suppliersData.post.length === 0) && (
                      <Box mt={isEdit ? 4 : ''}>
                        <Alert severity="info">
                          No Strategic Suppliers have been added to this
                          strategy
                        </Alert>
                      </Box>
                    )}
                    <SupplierDataGridByUnits
                      isEdit={isEdit}
                      suppliers={suppliersData.post}
                      unitGroups={unitGroups}
                      updateSupplierStatus={updateSupplierStatusFromGrid}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Box>

      <Box role="tabpanel" hidden={activeTab !== 1}>
        {activeTab === 1 && (
          <Box>
            {nonStrategicSuppliersData &&
            nonStrategicSuppliersData.post.length > 0 ? (
              <>
                <Alert
                  severity="informational"
                  sx={(theme) => ({ mt: theme.fuel6Tokens.JddcSpacingSmall })}
                >
                  Target business opportunities to resource to Strategic
                  Suppliers
                </Alert>
                <NonStrategicSupplierDataGrid
                  apiRef={nonStrategicSupplierDataGridApiRef}
                  isEdit={isEdit}
                  strategicSuppliers={nonStrategicSuppliersData.post}
                  unitGroups={unitGroups}
                  updateNonStrategicSupplierComments={
                    updateNonStrategicSupplierCommentsFromGrid
                  }
                  updateNonStrategicSupplierStatus={
                    updateNonStrategicSupplierStatusFromGrid
                  }
                  dispatchSupplier={dispatchSupplier}
                  dispatchNonStrategicSupplier={dispatchNonStrategicSupplier}
                  setNonStrategicSuppliers={setNonStrategicSuppliersList}
                  setNonSupplier={setNonSupplier}
                  nonSupplier={nonSupplier}
                />
              </>
            ) : (
              <Alert severity="informational" sx={{ maxWidth: '30rem' }}>
                There is currently no spend with Non-Strategic Suppliers for
                this strategy
              </Alert>
            )}
          </Box>
        )}
      </Box>

      {!isCreate && (
        <Box role="tabpanel" hidden={activeTab !== 2}>
          {activeTab === 2 && (
            <Box>
              <ProspectiveSupplierDataGrid
                commonProps={commonProps}
                setSuppliersDirty={setSuppliersDirty}
                setSuppliersMessage={setSuppliersMessage}
                isEdit={isEdit}
                noRowsMessage={
                  'No Prospective Suppliers have been added to this strategy'
                }
              />
            </Box>
          )}
        </Box>
      )}
      {supplierDetails && showSupplierCommentsModal && (
        <SupplierCommentsPopup
          showModal={showSupplierCommentsModal}
          handleCancel={handleModalCancel}
          supplier={supplierDetails}
        />
      )}
      {(showSupplierModal || showModalEdit) && (
        <SupplierPopup
          showModal={showSupplierModal || showModalEdit}
          handleCancel={() => {
            setShowSupplierModal(false);
            setShowModalEdit(false);
            setSupplier(null);
          }}
          setStatusErrorMsg={setStatusErrorMsg}
          dispatchSupplier={dispatchSupplier}
          setSuppliersDirty={setSuppliersDirty}
          setRefreshSupplierSpend={setRefreshSupplierSpend}
          setSuppliersMessage={setSuppliersMessage}
          hasDuplicateSuppliers={hasDuplicateSuppliers}
          supplier={supplier}
          userInfoMap={userInfoMap}
          dispatchNonStrategicSupplier={dispatchNonStrategicSupplier}
          isEditNonStretegicSupplier={editNonStrategic}
          setNonStrategicSuppliers={setNonStrategicSuppliersList}
          nonSupplier={nonSupplier}
          strategyInformation={strategyInformation}
          setNonSupplier={setNonSupplier}
          enforceCommentsWhenSupplierTypeChanged={
            !donotEnforceCommentsWhenSupplierTypeChanged
          }
          commonProps={commonProps}
        />
      )}

      {showModalEditNonStrategicSupplier && (
        <SupplierPopup
          showModal={showModalEditNonStrategicSupplier}
          handleCancel={() => {
            setShowModalEditNonStrategicSupplier(false);
            setNonStrategicSuppliersData && setNonStrategicSuppliersData(null);
            setSupplier(null);
          }}
          setStatusErrorMsg={setStatusErrorMsg}
          dispatchSupplier={dispatchSupplier}
          setSuppliersDirty={setSuppliersDirty}
          setRefreshSupplierSpend={setRefreshSupplierSpend}
          setSuppliersMessage={setSuppliersMessage}
          hasDuplicateSuppliers={hasDuplicateSuppliers}
          supplier={supplier}
          userInfoMap={userInfoMap}
          dispatchNonStrategicSupplier={dispatchNonStrategicSupplier}
          isEditNonStretegicSupplier={editNonStrategic}
          isNonStrategicSupplier={true}
          setNonStrategicSuppliers={setNonStrategicSuppliersList}
          nonSupplier={nonSupplier}
          strategyInformation={strategyInformation}
          setNonSupplier={setNonSupplier}
          enforceCommentsWhenSupplierTypeChanged={
            !donotEnforceCommentsWhenSupplierTypeChanged
          }
          editGrid={true}
          commonProps={commonProps}
        />
      )}
    </>
  );
}

function TabLabel({ label, count }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {label}
      <Chip
        color="informational"
        variant="badge"
        label={count ?? 0}
        size="small"
      />
    </Box>
  );
}
